import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function useReady(delay = 60) {
  const [ready, setReady] = useState(prefersReducedMotion);
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const timer = setTimeout(() => setReady(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  return ready;
}

function useCount(to, dur = 1100, delay = 0) {
  const [value, setValue] = useState(() => (prefersReducedMotion() ? to : 0));
  useEffect(() => {
    if (prefersReducedMotion()) { setValue(to); return undefined; }
    let raf;
    let start;
    const tick = (ts) => {
      start = start ?? ts;
      const p = Math.min(1, (ts - start) / dur);
      setValue(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    const begin = setTimeout(() => { raf = requestAnimationFrame(tick); }, delay);
    const finish = setTimeout(() => setValue(to), delay + dur + 50);
    return () => { clearTimeout(begin); clearTimeout(finish); cancelAnimationFrame(raf); };
  }, [to, dur, delay]);
  return value;
}

export function Num({ to, dec = 0, suf = "", delay = 0 }) {
  const value = useCount(to, 1100, delay);
  return <>{value.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec })}{suf}</>;
}

export function Spark({ data, w = 120, h = 36, color = "var(--color-accent-violet)", delay = 0 }) {
  const ready = useReady(delay);
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const pts = data.map((d, i) => [i / (data.length - 1) * w, h - 3 - (d - min) / (max - min || 1) * (h - 6)]);
  const path = pts.map((q, i) => `${i ? "L" : "M"}${q[0].toFixed(1)} ${q[1].toFixed(1)}`).join("");
  const last = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ overflow: "visible" }}>
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" pathLength="1" strokeDasharray="1" strokeDashoffset={ready ? 0 : 1} style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.3,.7,.2,1)" }} />
      <circle cx={last[0]} cy={last[1]} r="3.5" fill={color} style={{ opacity: ready ? 1 : 0, transition: "opacity .3s 1.1s" }} />
    </svg>
  );
}

export function Ring({ pct, size = 64, sw = 7, color = "var(--color-success)", children, delay = 0 }) {
  const ready = useReady(delay);
  const radius = (size - sw) / 2;
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div style={{ position: "relative", width: size, height: size, flex: "none" }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--color-surface-press-light)" strokeWidth={sw} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" pathLength="100" strokeDasharray="100" strokeDashoffset={ready ? 100 - clamped : 100} style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.3,.7,.2,1)" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>{children}</div>
    </div>
  );
}

export function LineChart({ labels, series, h = 240, ariaLabel = "Enrollments and completions by month" }) {
  const ref = useRef(null);
  const [w, setW] = useState(640);
  const [hov, setHov] = useState(null);
  const ready = useReady(200);
  useLayoutEffect(() => { if (ref.current) setW(ref.current.getBoundingClientRect().width || 640); }, []);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setW(entry.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const pad = { l: 36, r: 12, t: 12, b: 28 };
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const max = Math.max(50, Math.ceil(Math.max(...series.flatMap((s) => s.data)) / 50) * 50);
  const x = (i) => pad.l + i / Math.max(1, labels.length - 1) * iw;
  const y = (v) => pad.t + ih - v / max * ih;
  const path = (d) => d.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join("");
  const move = (e) => {
    const b = ref.current.getBoundingClientRect();
    const i = Math.round((e.clientX - b.left - pad.l) / iw * (labels.length - 1));
    setHov(i >= 0 && i < labels.length ? i : null);
  };
  return (
    <div ref={ref} style={{ position: "relative" }} onMouseMove={move} onMouseLeave={() => setHov(null)}>
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {[0, max / 2, max].map((t) => <g key={t}><line x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} stroke="var(--color-hairline-cloud)" strokeDasharray={t ? "3 4" : ""} /><text x={pad.l - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="currentColor" opacity=".5" fontFamily="var(--font-mono)">{t}</text></g>)}
        {labels.map((l, i) => <text key={`${l}-${i}`} x={x(i)} y={h - 8} textAnchor="middle" fontSize="11" fill="currentColor" opacity={hov === i ? 1 : 0.5}>{l}</text>)}
        <path d={`${path(series[0].data)}L${x(labels.length - 1)} ${y(0)}L${x(0)} ${y(0)}Z`} fill={series[0].color} style={{ opacity: ready ? 0.08 : 0, transition: "opacity .8s .6s" }} />
        {series.map((s, si) => <path key={s.name} d={path(s.data)} fill="none" stroke={s.color} strokeWidth={si ? 2 : 2.5} strokeLinecap="round" strokeLinejoin="round" pathLength="1" style={{ strokeDasharray: 1, strokeDashoffset: ready ? 0 : 1, transition: `stroke-dashoffset 1.4s ${si * 0.2}s cubic-bezier(.3,.7,.2,1)` }} />)}
        {hov != null && <g><line x1={x(hov)} x2={x(hov)} y1={pad.t} y2={pad.t + ih} stroke="var(--color-ink)" strokeOpacity=".2" />
          {series.map((s) => <circle key={s.name} cx={x(hov)} cy={y(s.data[hov])} r="4.5" fill="#fff" stroke={s.color} strokeWidth="2.5" />)}</g>}
      </svg>
      {hov != null && <div className="tip" style={{ left: Math.min(Math.max(x(hov), 70), w - 70), top: 0 }}>
        <b>{labels[hov]}</b>{series.map((s) => <span key={s.name}><i style={{ background: s.color }}></i>{s.name}<em>{s.data[hov]}</em></span>)}</div>}
    </div>
  );
}

export function Columns({ weeks, cats, h = 170 }) {
  const ready = useReady(300);
  const [hov, setHov] = useState(null);
  const totals = weeks.map((w) => cats.reduce((a, c) => a + (w.v[c.k] || 0), 0));
  const max = Math.max(1, ...totals);
  return (
    <div className="cols" style={{ height: h }} onMouseLeave={() => setHov(null)}>
      {weeks.map((w, i) => <div key={i} className={`col${hov === i ? " on" : ""}`} onMouseEnter={() => setHov(i)}>
        <div className="stack" style={{ height: ready ? `${totals[i] / max * 100}%` : "0%", transitionDelay: `${i * 40}ms` }}>
          {cats.map((c) => w.v[c.k] ? <i key={c.k} style={{ flex: w.v[c.k], background: c.color }}></i> : null)}</div>
        <span className="cl">{w.l}</span>
        {hov === i && <div className="tip sm"><b>{w.range}</b>{cats.filter((c) => w.v[c.k]).map((c) => <span key={c.k}><i style={{ background: c.color }}></i>{c.name}<em>{w.v[c.k]}</em></span>)}</div>}
      </div>)}
    </div>
  );
}

export function LoadBars({ rows, cap = 40, scale = 50 }) {
  const ready = useReady(350);
  return (
    <div className="load">{rows.map((row, i) => {
      const over = row.h > cap;
      return <div key={row.n} className="lr">
        <span className="ln">{row.n}</span>
        <div className="lt"><i className={over ? "over" : ""} style={{ width: ready ? `${Math.min(row.h / scale, 1) * 100}%` : 0, transitionDelay: `${i * 70}ms` }}></i><b style={{ left: `${cap / scale * 100}%` }}></b></div>
        <span className={`lh${over ? " over" : ""}`}>{row.h}h</span></div>;
    })}
      <div className="lr lg"><span></span><span className="lgd"><i></i>Scheduled hours this week<b></b>{cap}h capacity</span><span></span></div>
    </div>
  );
}
